import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getLocationColumns,
    getLocationFilterItems,
    getLocationFilterToggles,
} from '../components/Locations/location-list-config'
import { getLocations } from '../services'

interface LocationListFilters {
    type: string
    is_active: string
}

const FILTER_DEFAULTS: LocationListFilters = {
    type: '',
    is_active: '',
}

export const useLocationList = () => {
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<LocationListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<LocationListFilters>({
        defaultValues: filters,
    })

    const fetchFn = useCallback(() => getLocations(queryParams), [queryParams])

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_LOCATIONS, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const columns = useMemo(() => getLocationColumns(), [])
    const filterItems = useMemo(() => getLocationFilterItems(), [])
    const filterToggles = useMemo(() => getLocationFilterToggles(), [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        locations: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        refetch,
    }
}
